// Usage/billing tab: per-day usage, model mix, and totals from
// GET /sites/{id}/usage (SPEC_SITE_MANAGER_LOGS Capability 3).
import { useSiteUsage } from '../hooks';
import { QueryState } from '../../../components/QueryState';
import { formatInt, formatUsd } from '../../../lib/format';
import type { SiteUsageResponse } from '../../../lib/types';

const ID = 'em-mgr--site--usage';

export function UsageTab({ siteId }: { siteId: string }) {
  const { data, isPending, isError, error } = useSiteUsage(siteId);
  return (
    <QueryState<SiteUsageResponse>
      idBase={ID}
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(u) => (
        <div data-id={ID} className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Tile label="Total tokens" value={formatInt(u.totalTokens)} />
            <Tile label="Est. cost" value={formatUsd(u.totalEstCostUsd)} />
          </div>

          <p data-id={`${ID}--tokens-note`} className="text-xs text-slate-500 dark:text-slate-400">
            Total tokens counts input + output + cache-creation + cache-read. The per-day and
            per-model cache columns below reconcile to that total.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
              {u.days.length === 0 ? (
                <div
                  data-id={`${ID}--days-empty`}
                  className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400"
                >
                  No usage recorded in this window.
                </div>
              ) : (
                <table data-id={`${ID}--days-table`} className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                    <tr>
                      <th className="px-3 py-2">Day</th>
                      <th className="px-3 py-2">Turns</th>
                      <th className="px-3 py-2">Errors</th>
                      <th className="px-3 py-2">In</th>
                      <th className="px-3 py-2">Out</th>
                      <th className="px-3 py-2">Cache write</th>
                      <th className="px-3 py-2">Cache read</th>
                      <th className="px-3 py-2">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.days.map((d) => (
                      <tr key={d.date} data-id={`${ID}--day`} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="px-3 py-2">{d.date}</td>
                        <td className="px-3 py-2">{d.turns}</td>
                        <td className="px-3 py-2">{d.errors}</td>
                        <td className="px-3 py-2">{formatInt(d.inputTokens)}</td>
                        <td className="px-3 py-2">{formatInt(d.outputTokens)}</td>
                        <td data-id={`${ID}--day--cache-write`} className="px-3 py-2">
                          {formatInt(d.cacheCreationInputTokens)}
                        </td>
                        <td data-id={`${ID}--day--cache-read`} className="px-3 py-2">
                          {formatInt(d.cacheReadInputTokens)}
                        </td>
                        <td className="px-3 py-2">{formatUsd(d.estCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
              {u.byModel.length === 0 ? (
                <div
                  data-id={`${ID}--models-empty`}
                  className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400"
                >
                  No model activity in this window.
                </div>
              ) : (
                <table data-id={`${ID}--models-table`} className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                    <tr>
                      <th className="px-3 py-2">Model</th>
                      <th className="px-3 py-2">Turns</th>
                      <th className="px-3 py-2">In</th>
                      <th className="px-3 py-2">Out</th>
                      <th className="px-3 py-2">Cache write</th>
                      <th className="px-3 py-2">Cache read</th>
                      <th className="px-3 py-2">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.byModel.map((m) => (
                      <tr key={m.model} data-id={`${ID}--model`} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="px-3 py-2 font-medium">{m.model}</td>
                        <td className="px-3 py-2">{m.turns}</td>
                        <td className="px-3 py-2">{formatInt(m.inputTokens)}</td>
                        <td className="px-3 py-2">{formatInt(m.outputTokens)}</td>
                        <td data-id={`${ID}--model--cache-write`} className="px-3 py-2">
                          {formatInt(m.cacheCreationInputTokens ?? 0)}
                        </td>
                        <td data-id={`${ID}--model--cache-read`} className="px-3 py-2">
                          {formatInt(m.cacheReadInputTokens ?? 0)}
                        </td>
                        <td className="px-3 py-2">{formatUsd(m.estCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </QueryState>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 px-4 py-3 dark:border-slate-800">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}
